import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import * as Yup from "yup";
import { ERROR_MESSAGES, CIVILITY_MADAME_CODE, CIVILITY_MONSIEUR_CODE } from '../../const/AppConst';
import useAppState from '../../context/AppContext';
import MgpMessage from "../../elements/MgpMessage/MgpMessage";
import LoaderModal from "../../modals/common/LoaderModal";
import { IAdditionalData, relationType as relationTypeModel } from "../../context/model";
import { useFetchNomenclature } from "../../hooks/useFetchNomenclature";
import { isValidDate, calculNirKey, getAge, getValueNoSpace, formattedAttachmentOrganization, formattedNir } from "../../utils/utils";
import moment from 'moment';
import InfoBeneficiaryComponent, { InfoBeneficiaryStep1FormData, InfoBeneficiaryStep1FormDataProps } from "../info-beneficiary/InfoBeneficiaryComponent";
import StickButtonsBloc from "../../blocs/common/StickButtonsBloc";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../modals/common/ConfirmModal";
import { getAllIndexByString } from "../../utils/utils";

interface InfoBeneficiaryTemplateProps {
    nextUrl: string;
    previousUrl: string
}
const errMsg = "La relation et/ou l’âge de vos ayants droit ne correspond pas à votre déclaration. Le tarif proposé peut différer lors de la validation de votre contrat."

export default function InfoBeneficiaryTemplate({ nextUrl, previousUrl } : Readonly<InfoBeneficiaryTemplateProps>) {
  const [errorMsg, setErrorMsg] = useState<boolean>(false);
  const [errorMsgBeneficiaries, setErrorMsgBeneficiaries] = useState<boolean>(false);
  const [errorMsgInvalid, setErrorMsgInvalid] = useState<string | null>();
  const [errorMsgLimitAge, setErrorMsgLimitAge] = useState<string | null>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedBeneficiary, setSelectedBeneficiary] = useState<InfoBeneficiaryStep1FormDataProps>();

  const [index, setIndex] = useState<number>(0);
  const [isNew, setIsNew] = useState<boolean>(false);
  const [error, setError] = useState<number[]>([]);
  const [warningMsg, setWarningMsg] = useState<string| undefined>(undefined);
  const [isShowModal, setIsShowModal] = useState(false);

  useEffect(()=>{
    if(warningMsg === errMsg){
      setIsShowModal(true)
    }
  }, [warningMsg])

  const {
    state: { updatedProposition, additionalData, proposition, socialSecurityDocuments },
    dispatch,
  } = useAppState();

  const navigate = useNavigate();

  useFetchNomenclature("civility");

  let beneficiaryPropsMap = [] as InfoBeneficiaryStep1FormDataProps[];
  // if there is no AD in updatedProposition, 
  // need to take initial AD from RO doc + attes de droits (from socialSecurityDocuments)
  if(updatedProposition?.beneficiaries && updatedProposition.beneficiaries.length ===0 ){
    // take all ADs except the user
    console.log('socialSecurityDocuments', socialSecurityDocuments)
    const benesFromDocs = socialSecurityDocuments
      ?.map(doc=> doc?.extractedData?.beneficiaries?.filter(person=> person && person.isPrincipalBeneficiary !== true))
      .flat();

    //remove duplicated beneficiaries with same socialSecurityNumber 
    const arr = new Set();
    console.log('benesFromDocs', benesFromDocs)
    const uniqBenesBySecurityNumber = benesFromDocs && benesFromDocs?.filter(el => {
      console.log('el', el)
      const duplicate = arr.has(el?.socialSecurityNumber);
      arr.add(el?.socialSecurityNumber);
      return !duplicate;
    });
    
    if(socialSecurityDocuments?.[0]?.extractedData?.beneficiaries?.length >0){
      beneficiaryPropsMap = uniqBenesBySecurityNumber.map((extractedInfo) => {   
        const beneficiaryProps = {
          onModify: false,
          relationType: getAge(extractedInfo.birthDate) < 18 ? 'CHILD' : '',
          contact: {
            nir:formattedNir(extractedInfo.nir),
            nirKey: extractedInfo.nirKey,
            lastName:extractedInfo.lastName,
            firstName: extractedInfo.firstName,
            birthDate: extractedInfo.birthDate,
            attachmentOrganization: formattedAttachmentOrganization(extractedInfo?.socialSecurityOrg),
            civilityCode: extractedInfo.nir?.at(0) === "1" ? CIVILITY_MONSIEUR_CODE : CIVILITY_MADAME_CODE,

          },

        } as InfoBeneficiaryStep1FormDataProps;
        
        return beneficiaryProps;
    });
    }
  }
  
  // if there is AD in updatedPropo, take those
  if(updatedProposition?.beneficiaries && updatedProposition.beneficiaries.length > 0) {
    beneficiaryPropsMap = updatedProposition?.beneficiaries?.map((extractedInfo, index) => {   
      const beneficiaryProps = {
        withBeneficiary: extractedInfo.isConfirmed && extractedInfo.relationType,
        onModify: false,
        contact: {
          ...extractedInfo.contact,
          attachmentOrganization: formattedAttachmentOrganization(extractedInfo?.contact?.attachmentOrganization),
          nir: formattedNir(extractedInfo?.contact?.nir)
        },
        relationType: extractedInfo.relationType,
        isConfirmed: extractedInfo.isConfirmed
      } as InfoBeneficiaryStep1FormDataProps;
      
      return beneficiaryProps;
    });
  }
  const validationSchema = Yup.object().shape({
    ayantDroits: Yup.array()
    .of(
      Yup.object().shape({
        relationType: Yup.string().nullable()
        .when('onModify', {
          is: (onModify: boolean) => onModify,
          then: Yup.string().required(ERROR_MESSAGES.requiredMsg)
        }),
        contact: Yup.object().when('onModify', {
          is: (onModify: boolean) => onModify,
          then: Yup.object().shape({
            civilityCode: Yup.string()
            .required(ERROR_MESSAGES.requiredMsg),
            firstName: Yup.string()
              .required(ERROR_MESSAGES.requiredMsg)
              .min(2, ERROR_MESSAGES.invalidMsg)
              .max(50, ERROR_MESSAGES.invalidMsg)
              .matches(/^[a-zA-Z '-]+$/i, ERROR_MESSAGES.invalidName)
              .test('invalidCombination', ERROR_MESSAGES.invalidMsg, (val)=> {
                let invalidCase = val?.includes('--') === true ;
                let isDashInTheMiddle;
                if(val){
                  const indexList = getAllIndexByString(val, "-");
                  isDashInTheMiddle = indexList.indexOf(0) !== -1 || indexList.indexOf(val.length-1) !== -1;
                }
                return !invalidCase && !isDashInTheMiddle;
              }),
            lastName: Yup.string()
              .required(ERROR_MESSAGES.requiredMsg)
              .min(2, ERROR_MESSAGES.invalidMsg)
              .max(50, ERROR_MESSAGES.invalidMsg)
              .matches(/^[a-zA-Z '-]+$/i, ERROR_MESSAGES.invalidName)
              .test('invalidCombination', ERROR_MESSAGES.invalidMsg, (val)=> {
                let invalidCase = val?.includes('--') === true ;
                let isDashInTheMiddle;
                if(val){
                  const indexList = getAllIndexByString(val, "-");
                  isDashInTheMiddle = indexList.indexOf(0) !== -1 || indexList.indexOf(val.length-1) !== -1;
                }
                return !invalidCase && !isDashInTheMiddle;
              }),
            birthDate: Yup.string()
              .required(ERROR_MESSAGES.requiredMsg)
              .test("validDate", ERROR_MESSAGES.invalidMsg, (val) => {
                let now = new Date();
                now.setHours(0,0,0,0);
                return val !== undefined && isValidDate(val) && new Date(val) < now;
              }),
           
           nir: Yup.string().required(ERROR_MESSAGES.requiredMsg)
              .min(13, ERROR_MESSAGES.invalidMsg)
              .max(18, ERROR_MESSAGES.invalidMsg)
              .when('civilityCode', (civilityCode, schema) => {
                return schema.when('birthDate', (birthDate: any) => {
                  if(getAge(birthDate) >= 16){
                    return schema.test("follow_civility", "La valeur ne correspond pas à sa civilité", (val:string) => {
                      const valueNoSpace = getValueNoSpace(val);
                      return (civilityCode === CIVILITY_MONSIEUR_CODE && valueNoSpace?.startsWith("1") || 
                      civilityCode === CIVILITY_MONSIEUR_CODE && valueNoSpace?.startsWith("7") || 
                      civilityCode === CIVILITY_MADAME_CODE && valueNoSpace?.startsWith("2") || 
                      civilityCode === CIVILITY_MADAME_CODE && valueNoSpace?.startsWith("8"));
                    });
                  }
                })
              })
              .when('birthDate', (birthDate, schema) => {
                  if(birthDate && moment(birthDate).isValid()){
                    if(getAge(birthDate) >= 16){          
                      return schema.test("follow-birthDate", "La valeur ne correspond pas à sa date de naissance", (val:string) => { 
                        const valueNoSpace = getValueNoSpace(val);
                        const isProvisoire = valueNoSpace.startsWith("7") || valueNoSpace.startsWith("8");
                        if (isProvisoire) return true;
                        const fourthChar = valueNoSpace?.charAt(3);
                        if (fourthChar && Number(fourthChar) > 1) return true;
                        return valueNoSpace && 
                        birthDate.substring(5, 7) === valueNoSpace.substring(3,5) && 
                        birthDate.substring(2, 4) === valueNoSpace.substring(1,3); 
                      }); 
                    }
                  }        
                  return schema;
              }),
             nirKey: Yup.string()
                .required(ERROR_MESSAGES.requiredMsg)
                .min(2, ERROR_MESSAGES.invalidMsg)
                .max(2, ERROR_MESSAGES.invalidMsg)
                .when('nir', (nir, schema) => {
                return schema.test("nir-val", ERROR_MESSAGES.invalidMsg, (val: string) => {
                  const valueNoSpace = getValueNoSpace(val);
                  const nirNoSpace = getValueNoSpace(nir);
                  return valueNoSpace && 
                  Number(valueNoSpace) && 
                  nirNoSpace && 
                  valueNoSpace === calculNirKey(nirNoSpace);
                  }); 
              }),
            attachmentOrganization: Yup.string().required(ERROR_MESSAGES.requiredMsg)
            .min(9, ERROR_MESSAGES.invalidMsg)
            .max(11, ERROR_MESSAGES.invalidMsg)
            .matches(/^[0-9, ]+$/i, ERROR_MESSAGES.invalidMsg),
          })
        }),
      })
    )
  });    

  const {
    register,
    handleSubmit,
    setValue, 
    control, 
    watch, 
    getValues,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<InfoBeneficiaryStep1FormData>({
    defaultValues: {
      ayantDroits: beneficiaryPropsMap
    },
    mode: "onBlur",
    reValidateMode: "onChange",
    resolver: yupResolver(validationSchema),
  });

  const { fields, append, remove, update } = useFieldArray({
    name: "ayantDroits",
    control
  });

  
  const handleOpenForm = (e: any) => {
    e.preventDefault();
    const beneficiaryProps = {
      withBeneficiary: true,
      onModify: true,
      isConfirmed: false,
      contact: {
        firstName: '',
        lastName: '',
        birthDate: '',
        civilityCode: '',
        regimeCode: '',
        attachmentOrganization: '',
        nir: '',
        nirKey: ''
      }
    } as InfoBeneficiaryStep1FormDataProps;

    append(beneficiaryProps);
    setIsOpen(true);
    setSelectedBeneficiary(beneficiaryProps);
    setIndex(fields.length);
    setIsNew(true);
    dispatch({type: 'SHOW_MODAL', showModal: true});
  }

  const handleOpenModal = (e: any, beneficiary: InfoBeneficiaryStep1FormDataProps, i: number) => {
    e.preventDefault();
    setIsOpen(true);
    setSelectedBeneficiary(beneficiary);
    setValue(`ayantDroits.${i}.onModify`, true);
    setIndex(i);
    dispatch({type: 'SHOW_MODAL', showModal: true});
  }

  const handleDeleteAyantDroit = (i: number) => {
    remove(i);
    setIsNew(false);
    setErrorMsg(false);
    const newAyantDroits = getValues('ayantDroits');
    
    const arrBeneficiaries = newAyantDroits.filter(el => el.relationType === relationTypeModel.CONJOINT || el.relationType === relationTypeModel.CONCUBIN);
    if (arrBeneficiaries.length > 1 ) {
      setErrorMsgBeneficiaries(true);
    } else {
      setErrorMsgBeneficiaries(false);
    }
  }

  const onConfirmAction = () => {
    setErrorMsgLimitAge("");
    const newBeneficiarie = getValues(`ayantDroits.${index}`);
    if(newBeneficiarie.relationType === relationTypeModel.CHILD && 
      !newBeneficiarie.contact.hasHandicap && 
      getAge(newBeneficiarie.contact.birthDate, updatedProposition?.effectiveDate ?? undefined) >= 25) {
      setErrorMsgLimitAge("Cet ayant droit n'est pas éligible en raison de son âge.");
    } 
    else {
      setIsOpen(false);
      setErrorMsgInvalid(null);
      dispatch({type: 'SHOW_MODAL', showModal: false});
      if (selectedBeneficiary) {
        const data = {...selectedBeneficiary}
        data.withBeneficiary = true;
        data.onModify = false;
        data.isConfirmed = true;
        data.relationType = getValues(`ayantDroits.${index}.relationType`);
        data.contact = getValues(`ayantDroits.${index}.contact`);   
        const newAyantDroits = getValues('ayantDroits');
        const arrBeneficiaries = newAyantDroits.filter((el: any) => el.relationType === relationTypeModel.CONJOINT || el.relationType === relationTypeModel.CONCUBIN);
        if (arrBeneficiaries.length > 1 ) {
          setErrorMsgBeneficiaries(true);
        } else {
          setErrorMsgBeneficiaries(false);
        }
        update(index, data);
      }
      setIsNew(false);
      const indexError = error.indexOf(index);
      if (indexError !== -1) {
        const newErrorArr = [...error];
        newErrorArr.splice(indexError, 1);
        setError(newErrorArr);      
        const newAyantDroits = getValues('ayantDroits');
        const arrWithBeneficiary = newAyantDroits.filter((el: any) => el.withBeneficiary === false);
        if(arrWithBeneficiary.length > 0) {
          setErrorMsg(true);
        } else {
          setErrorMsg(false);
        }
      }
    }
  }

  const onRefuseAction = () => {
    setIsOpen(false);
    setErrorMsgLimitAge("");
    dispatch({type: 'SHOW_MODAL', showModal: false});
    const currentValue = {...selectedBeneficiary};
    currentValue.onModify = false;
    setValue(`ayantDroits.${index}`, currentValue as InfoBeneficiaryStep1FormDataProps);
    clearErrors()
    if (isNew) {
      remove(index);
      setIsNew(false);
    }
  }
  const warningCheck = (beneficiaries:InfoBeneficiaryStep1FormData ) => {  
    setWarningMsg(undefined)
    const arrBeneficiaries = beneficiaries.ayantDroits?.filter(el => el.relationType === relationTypeModel.CONJOINT || el.relationType === relationTypeModel.CONCUBIN);
    const arrBeneChild = beneficiaries.ayantDroits?.filter(el => el.relationType === relationTypeModel.CHILD);

    //check if birthday of children is the same
    let childBirthdateCheck = true
    additionalData?.children?.forEach(child => {
      if(arrBeneChild.findIndex(el=> el.contact.birthDate === child.birthDate) ===-1){
        childBirthdateCheck = false}
    })

    //check if hasAlsaceMoselle (hasAM) value is right for each child
    let hasAMUpdatedChild = arrBeneChild
      .map(child=> `${child?.contact?.birthDate}-${child?.contact?.alsaceMoselleRegime}`)

    const hasAMAdditionalChildren = additionalData?.children
      ?.map(child => `${child?.birthDate}-${child?.hasAlsaceMoselle}`)

    let childHasAMCheck = true;

    hasAMAdditionalChildren?.forEach(child=>{
      const foundIndex= hasAMUpdatedChild.findIndex(el => el ===child)
      if( foundIndex=== -1){ 
        childHasAMCheck = false
      } else {
        hasAMUpdatedChild.splice(foundIndex, 1)
      }
    })

    
    // check birthday of conjoint (from My Situation page, from Déclarez vos ayants droit)
    if((additionalData?.hasPartner && 
        arrBeneficiaries?.length ===1 && 
        additionalData?.partnerInfo?.birthDate !== arrBeneficiaries[0].contact?.birthDate
    )
    // check having conjoint (from My Situation page, from Déclarez vos ayants droit)
    || (!additionalData?.hasPartner && arrBeneficiaries?.length ===1  )
    // check children number is the same (from My Situation page, from Déclarez vos ayants droit)
    ||((additionalData?.hasChild || arrBeneChild?.length>0) && 
      additionalData?.children?.length !== arrBeneChild?.length
    )
    // check birthday of children (from My Situation page, from Déclarez vos ayants droit)
    || (additionalData?.hasChild && 
      additionalData?.children?.length === arrBeneChild?.length && 
      !childBirthdateCheck
    )
    // check if value of hasAlsace Moselle of children is the same for each child (from My Situation page, from Déclarez vos ayants droit)
    || (additionalData?.hasChild && 
      additionalData?.children?.length === arrBeneChild?.length && 
      !childHasAMCheck
    )){
      setWarningMsg(errMsg); 
    } else {
      uploadAyantdroits(beneficiaries)
    }
  }

  const uploadAyantdroits = (beneficiaries:InfoBeneficiaryStep1FormData )=>{
    setErrorMsgInvalid(null);
    setIsShowModal(false)
    let tempUpdatedPropo = updatedProposition;
    let tempAdditional =  {} as IAdditionalData;

    if(tempUpdatedPropo) {
      const data = Object.entries(beneficiaries.ayantDroits).filter(([_, beneficiary]) => beneficiary.withBeneficiary).map( ([_, beneficiary]) => {
          return {
            relationType: beneficiary.relationType,
            contact: {
              ...beneficiary.contact,
              attachmentOrganization: getValueNoSpace(beneficiary.contact.attachmentOrganization),
              nir: getValueNoSpace(beneficiary.contact.nir),
              firstName: beneficiary.contact.firstName.replaceAll("'", ' '),
              lastName: beneficiary.contact.lastName.replaceAll("'", ' '),
            },
            isConfirmed: beneficiary.isConfirmed
          };
      })  
      tempUpdatedPropo.beneficiaries = data;

      // update additionalData
      const children = tempUpdatedPropo.beneficiaries?.filter(bene => bene.relationType === relationTypeModel.CHILD)
      const conjoint = tempUpdatedPropo.beneficiaries?.filter(bene => bene.relationType !== relationTypeModel.CHILD)

      tempAdditional.hasChild = children.length>0;
      tempAdditional.hasPartner = conjoint.length>0;
  
      tempAdditional.children = children.map(child => ({
        birthDate: child.contact.birthDate,
        hasAlsaceMoselle: child.contact.alsaceMoselleRegime,
        hasHandicap: child.contact?.hasHandicap,
      }))

      tempAdditional.partnerInfo = conjoint.map(child => ({
        birthDate: child.contact.birthDate,
        hasAlsaceMoselle: child.contact.alsaceMoselleRegime,
      }) )?.[0]
    }

    dispatch({
      type: 'NEXT_INFO_STEP', 
      path: nextUrl, 
      updatedProposition: tempUpdatedPropo,
      additionalData: tempAdditional
    });
    navigate({
      pathname: nextUrl,
      search: `?id=${proposition!.propositionId}`,
    }); 
  }

 const onNextAction = (beneficiaries: InfoBeneficiaryStep1FormData) => {
    setErrorMsg(false);
    setErrorMsgInvalid(null);
    const invalid = beneficiaries?.ayantDroits?.findIndex(el => !el.withBeneficiary);
    const arrBeneficiaries = beneficiaries?.ayantDroits
        ?.filter(el => el.relationType === relationTypeModel.CONJOINT || el.relationType === relationTypeModel.CONCUBIN);
    const arrBeneChild = beneficiaries?.ayantDroits
        ?.filter(el => el.relationType === relationTypeModel.CHILD);

    if(invalid !== -1) {
      setErrorMsg(true);
      let errorArr = [] as number[];
      beneficiaries?.ayantDroits?.forEach((el, i) => {
        if (!el.withBeneficiary) {
          errorArr.push(i);
        }
      });
      setError(errorArr);
    } else if(beneficiaries.ayantDroits.length < 1) {
      setErrorMsgInvalid('Veuillez ajouter un ayant droit ci-dessous.');
    } else if(arrBeneficiaries.length > 1) {
      setErrorMsgBeneficiaries(true);
    } else if(additionalData?.hasPartner && arrBeneficiaries.length < 1) {
      setErrorMsgInvalid('Vous avez coché l\'ajout d\'un conjoint précédemment. Renseignez ce dernier ou revenez en arrière et cocher "Non"');
    } else if(additionalData?.hasChild && arrBeneChild.length < 1) {
      setErrorMsgInvalid('Vous avez coché l\'ajout d\'enfant(s) précédemment. Renseignez ce(s) dernier(s) ou revenez en arrière et cocher "Non"');
    } 
    else {
      // check warning message, but not block next step
      warningCheck(beneficiaries)
    }
  }

  const onPreviousAction = () => {
    dispatch({type: 'BACK_INFO_STEP', path: previousUrl});
    navigate({
      pathname: previousUrl,
      search: `?id=${proposition!.propositionId}`,
    }); 
  }
  return (
    <form style={{width: '100%'}}>
      <div>
        {errorMsg && <MgpMessage type="danger" message="Veuillez consulter le(s) ayant(s) droit ci-dessous pour vérifier et confirmer les données renseignées." hasCloseBtn={false}/>}
        {errorMsgBeneficiaries && <MgpMessage type="danger" message="Vous avez déclaré plus d'un(e) conjoint(e) ou concubin(e). Veuillez corriger votre déclaration." hasCloseBtn={false}/>}
        {errorMsgInvalid && <MgpMessage type="danger" message={errorMsgInvalid} hasCloseBtn={false} />}
      </div>
        
      <div className='grid-flex jc-start'>
        {fields.map((field, i )=> 
          <div key={field.id} className={`m-y-sm card-contrats hover box ${error.includes(i) && 'box-error'} ${field.withBeneficiary && 'box-selected'}`}>
          <div className='card' style={{gap: 0, flexWrap: "nowrap"}}>
            <div className="grid-flex jc-space-arround jc-start" >
            <i className='icon-personne bleu fz-lg'></i>
            {field.withBeneficiary && <i className="icon-coche fz-lg" style={{marginLeft: "auto"}}></i>}
            </div>
            
            {field.relationType &&<p className=''>{field.relationType === relationTypeModel.CHILD ? "Enfant" : field.relationType === relationTypeModel.CONCUBIN ? "Concubin(e)" : "Conjoint(e)"}</p>}
            <p className='fz-lg fw-medium mt-md bleu'>{`${field.contact.firstName??''} ${field.contact.lastName??''}`}</p>
            <p>{moment(field.contact.birthDate).format("DD/MM/YYYY") || ''}</p>


            <div className="wrapper-cta-inline m-y-lg">
            {field.withBeneficiary ? <button className="cta-blanc cta-small" style={{border: "1px solid #144196"}} onClick={(e) => handleOpenModal(e, field, i)}>Modifier</button> : <button className="cta-bleu cta-small" onClick={(e) => handleOpenModal(e, field, i)}>Consulter</button>}
            </div>
            <div className="wrapper-cta-inline mt-lg">
              <button className="cta-blanc cta-small clickableEl" onClick={() => handleDeleteAyantDroit(i)}>Retirer cet ayant droit</button>
            </div>
          </div>
        </div>)}

        <div className='box card-contrats hover m-y-sm' >
          <div className='card'>
            <i className='icon-plus bleu fz-lg'></i>
            <div className="wrapper-cta-inline m-y-lg"><button className="cta-bleu cta-small" onClick={(e) => handleOpenForm(e)}>Ajouter un ayant droit</button></div>
          </div>
        </div>
      </div>
  
      {isOpen && 
      selectedBeneficiary && 
      <InfoBeneficiaryComponent
       isOpen={isOpen} 
       onCloseModal={() => {
        setIsOpen(false);
        dispatch({type: 'SHOW_MODAL', showModal: false});
      }}  
        onConfirmAction={handleSubmit(onConfirmAction)} 
        onRefuseAction={onRefuseAction}
        register={register} 
        errorMsgLimitAge={errorMsgLimitAge}
        errors={errors} 
        watch={watch} 
        setValue={setValue} 
        contact={selectedBeneficiary.contact} 
        relationType={selectedBeneficiary.relationType} 
        id={index} 
        isConfirmed={selectedBeneficiary.isConfirmed}
      />}

      <StickButtonsBloc 
        showPreviousButton={true} 
        disabledPreviousButton={false} 
        nextAction={handleSubmit(onNextAction)} 
        showNextButton={true} 
        previousAction={onPreviousAction}/>
      
      {isSubmitting&&<LoaderModal hasMessage={false}/>}
      {warningMsg && 
        <ConfirmModal 
          isOpen={isShowModal} 
          onConfirmAction={handleSubmit(uploadAyantdroits)} 
          onCloseModal={()=>{
            setIsShowModal(false);         
            setWarningMsg(undefined)
          }}
          
          text={<p className="ta-center bleu fz-lg fw-bold">
          {warningMsg}
            </p>} />
      }
    </form>
  )
}

